/** Shared, pure result rules. Distances are kilometres and times are seconds. */
export function isFinished(race) {
  return !!race && race.isDNF !== true && !(typeof race.time === 'string' && race.time.trim().toUpperCase() === 'DNF');
}

export function timeSeconds(race) {
  if (!isFinished(race) || race.isTimed === false || typeof race.time !== 'string') return null;
  const match = /^(\d+):([0-5]\d):([0-5]\d)$/.exec(race.time);
  if (!match) return null;
  const seconds = Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]);
  return Number.isSafeInteger(seconds) && seconds > 0 ? seconds : null;
}

export const positiveNumber = value => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

export function summarizeRaces(races = []) {
  let totalDistance = 0, totalElevation = 0, paceDistance = 0, totalTime = 0;
  for (const race of races) {
    if (!isFinished(race)) continue;
    const distance = positiveNumber(race.distance);
    totalDistance += distance;
    if (race.type === 'trail') totalElevation += positiveNumber(race.elevation);
    const seconds = timeSeconds(race);
    if (distance && seconds !== null) { paceDistance += distance; totalTime += seconds; }
  }
  return { raceCount: races.length, totalDistance, totalElevation, avgPace: paceDistance ? totalTime / paceDistance : null };
}

// Narrow tolerances accommodate customary rounding (21.1, 42.2, 80.5, 160.9).
// Broad chart buckets are intentionally separate from PB eligibility.
export const PB_DISTANCES = Object.freeze({
  '5K': 5, '10K': 10, 'Half Marathon': 21.0975, 'Marathon': 42.195,
  '50K': 50, '50 miles': 80.4672, '100K': 100, '100 miles': 160.9344,
});
export function personalBestCategory(race) {
  if (race?.type !== 'road' || timeSeconds(race) === null) return null;
  const distance = positiveNumber(race.distance);
  return Object.entries(PB_DISTANCES).find(([, km]) => Math.abs(distance - km) <= 0.051)?.[0] ?? null;
}
/** Map category to the winning original record. Equal times retain input order. */
export function getPersonalBests(races = []) {
  const bests = new Map();
  for (const race of races) {
    const category = personalBestCategory(race);
    if (category && (!bests.has(category) || timeSeconds(race) < timeSeconds(bests.get(category)))) bests.set(category, race);
  }
  return bests;
}

/** Strict calendar day only; year-only records must never acquire an invented day. */
export function fullRaceDate(race) {
  if (typeof race?.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(race.date)) return null;
  const date = new Date(`${race.date}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === race.date ? date : null;
}
export function raceYear(race) {
  if (typeof race?.date !== 'string') return null;
  if (/^\d{4}$/.test(race.date) || fullRaceDate(race)) return Number(race.date.slice(0, 4));
  return null;
}
